<template>
  <div>
    <!-- Header -->
    <b-card
      no-body
      class="mb-2"
    >
      <b-card-body class="d-flex align-items-center py-1">
        <h4 class="mb-0">
          <feather-icon
            icon="BarChart2Icon"
            size="20"
            class="text-primary mr-1"
          />
          Raporlar
        </h4>
        <b-button
          variant="outline-secondary"
          size="sm"
          class="ml-auto"
          :disabled="loading"
          @click="onRefresh"
        >
          <feather-icon icon="RefreshCwIcon" /> Yenile
        </b-button>
      </b-card-body>
    </b-card>

    <b-card no-body>
      <b-tabs
        v-model="activeTab"
        card
      >

        <!-- ───────── TAB 1: Özet ───────── -->
        <b-tab
          title="Özet"
          active
          @click="onTabSummary"
        >
          <div class="d-flex align-items-center mb-2">
            <small class="text-muted">PnL Timeline aralığı</small>
            <b-form-select
              v-model="timelineDays"
              :options="dayOptions"
              class="ml-auto"
              style="width:auto;"
              @change="fetchTimeline"
            />
          </div>

          <!-- ─── Summary cards ─────────────────────────────────────────────────── -->
          <b-row
            v-if="summary"
            class="mb-2"
          >
            <b-col
              cols="6"
              md="3"
              class="mb-1"
            >
              <b-card
                no-body
                class="bg-light-success"
              >
                <b-card-body class="py-1 text-center">
                  <small class="text-muted">Toplam Realize PnL</small>
                  <h4 :class="summary.totalRealizedPnl >= 0 ? 'text-success' : 'text-danger'">
                    {{ summary.totalRealizedPnl >= 0 ? '+' : '' }}{{ formatNum(summary.totalRealizedPnl, 4) }}
                  </h4>
                </b-card-body>
              </b-card>
            </b-col>
            <b-col
              cols="6"
              md="3"
              class="mb-1"
            >
              <b-card
                no-body
                class="bg-light-info"
              >
                <b-card-body class="py-1 text-center">
                  <small class="text-muted">Bugün</small>
                  <h4 :class="summary.todayPnl >= 0 ? 'text-success' : 'text-danger'">
                    {{ summary.todayPnl >= 0 ? '+' : '' }}{{ formatNum(summary.todayPnl, 4) }}
                  </h4>
                </b-card-body>
              </b-card>
            </b-col>
            <b-col
              cols="6"
              md="3"
              class="mb-1"
            >
              <b-card
                no-body
                class="bg-light-warning"
              >
                <b-card-body class="py-1 text-center">
                  <small class="text-muted">Son 7 Gün</small>
                  <h4 :class="summary.weekPnl >= 0 ? 'text-success' : 'text-danger'">
                    {{ summary.weekPnl >= 0 ? '+' : '' }}{{ formatNum(summary.weekPnl, 4) }}
                  </h4>
                </b-card-body>
              </b-card>
            </b-col>
            <b-col
              cols="6"
              md="3"
              class="mb-1"
            >
              <b-card
                no-body
                class="bg-light-primary"
              >
                <b-card-body class="py-1 text-center">
                  <small class="text-muted">Son 30 Gün</small>
                  <h4 :class="summary.monthPnl >= 0 ? 'text-success' : 'text-danger'">
                    {{ summary.monthPnl >= 0 ? '+' : '' }}{{ formatNum(summary.monthPnl, 4) }}
                  </h4>
                </b-card-body>
              </b-card>
            </b-col>

            <b-col
              cols="6"
              md="3"
              class="mb-1"
            >
              <b-card no-body>
                <b-card-body class="py-1 text-center">
                  <small class="text-muted">Toplam Sermaye (allocated)</small>
                  <h5>{{ formatNum(summary.totalCapital, 2) }}</h5>
                </b-card-body>
              </b-card>
            </b-col>
            <b-col
              cols="6"
              md="3"
              class="mb-1"
            >
              <b-card no-body>
                <b-card-body class="py-1 text-center">
                  <small class="text-muted">Açık Pozisyon</small>
                  <h5>{{ summary.openPositions }} ·
                    <span
                      :class="summary.totalUnrealizedPnl >= 0 ? 'text-success' : 'text-danger'"
                      class="font-small-3"
                    >
                      {{ summary.totalUnrealizedPnl >= 0 ? '+' : '' }}{{ formatNum(summary.totalUnrealizedPnl, 4) }}
                    </span>
                  </h5>
                </b-card-body>
              </b-card>
            </b-col>
            <b-col
              cols="6"
              md="3"
              class="mb-1"
            >
              <b-card no-body>
                <b-card-body class="py-1 text-center">
                  <small class="text-muted">Toplam Trade</small>
                  <h5>{{ summary.closedTrades }}/{{ summary.totalTrades }}<small class="text-muted ml-1">kapanan/total</small></h5>
                </b-card-body>
              </b-card>
            </b-col>
            <b-col
              cols="6"
              md="3"
              class="mb-1"
            >
              <b-card no-body>
                <b-card-body class="py-1">
                  <small class="text-muted d-block text-center mb-1">Bot Durumları</small>
                  <div class="text-center">
                    <b-badge
                      v-for="(count, status) in summary.botCounts"
                      :key="`bot-${status}`"
                      :variant="botStatusVariant(status)"
                      class="mr-1 mb-1"
                    >
                      {{ status }}: {{ count }}
                    </b-badge>
                  </div>
                </b-card-body>
              </b-card>
            </b-col>
          </b-row>

          <b-card
            v-if="performanceSummary"
            no-body
            class="mb-2"
          >
            <b-card-header class="d-flex align-items-center py-1">
              <h5 class="mb-0">
                <feather-icon icon="ActivityIcon" /> Canlı Performans
              </h5>
              <b-badge
                :variant="performanceBandVariant(performanceSummary.band)"
                class="ml-auto"
              >
                {{ performanceSummary.band }}
              </b-badge>
            </b-card-header>
            <b-card-body class="py-1">
              <b-alert
                :variant="performanceSummary.band === 'HEALTHY' ? 'success' : performanceSummary.band === 'WATCH' ? 'warning' : 'danger'"
                show
                class="mb-1"
              >
                {{ performanceSummary.note }}
              </b-alert>
              <b-row>
                <b-col
                  v-for="item in performanceSummaryCards"
                  :key="item.label"
                  cols="6"
                  md="3"
                  class="mb-1"
                >
                  <b-card no-body>
                    <b-card-body class="py-1 text-center">
                      <small class="text-muted">{{ item.label }}</small>
                      <h5 :class="item.className">
                        {{ item.value }}
                      </h5>
                    </b-card-body>
                  </b-card>
                </b-col>
              </b-row>
            </b-card-body>
          </b-card>

          <!-- ─── PnL Timeline + Equity Curve ──────────────────────────────────── -->
          <b-row class="mb-2">
            <b-col
              cols="12"
              lg="6"
            >
              <b-card class="h-100">
                <h5 class="mb-1">
                  <feather-icon icon="BarChartIcon" /> Günlük PnL ({{ timelineDays }} gün)
                </h5>
                <div
                  v-if="!pnlTimeline.length"
                  class="text-muted text-center py-3"
                >
                  Bu aralıkta trade yok.
                </div>
                <vue-apex-charts
                  v-else
                  type="bar"
                  height="260"
                  :options="pnlChartOptions"
                  :series="pnlChartSeries"
                />
              </b-card>
            </b-col>
            <b-col
              cols="12"
              lg="6"
            >
              <b-card class="h-100">
                <h5 class="mb-1">
                  <feather-icon icon="TrendingUpIcon" /> Equity Curve — Kümülatif PnL
                </h5>
                <div
                  v-if="!pnlTimeline.length"
                  class="text-muted text-center py-3"
                >
                  Veri yok.
                </div>
                <vue-apex-charts
                  v-else
                  type="area"
                  height="260"
                  :options="equityChartOptions"
                  :series="equityChartSeries"
                />
              </b-card>
            </b-col>
          </b-row>

          <!-- ─── Bot performance table ─────────────────────────────────────────── -->
          <b-card no-body>
            <b-card-header class="d-flex align-items-center py-1">
              <h5 class="mb-0">
                <feather-icon icon="CpuIcon" /> Bot Performansı
              </h5>
              <small class="ml-auto text-muted">PnL'ye göre sıralı</small>
            </b-card-header>
            <b-table
              :items="botPerformance"
              :fields="botFields"
              small
              striped
              responsive
              show-empty
              empty-text="Bot performans verisi yok."
            >
              <template #cell(name)="data">
                <strong>{{ data.item.name }}</strong>
                <div class="font-small-2 text-muted">
                  <b-badge
                    :variant="botStatusVariant(data.item.status)"
                    class="mr-1"
                  >
                    {{ data.item.status }}
                  </b-badge>
                  <b-badge :variant="data.item.mode === 'LIVE' ? 'danger' : 'info'">
                    {{ data.item.mode }}
                  </b-badge>
                  · {{ data.item.symbol || '—' }} · {{ data.item.timeframe || '—' }}
                </div>
              </template>
              <template #cell(realizedPnl)="data">
                <strong :class="data.item.realizedPnl >= 0 ? 'text-success' : 'text-danger'">
                  {{ data.item.realizedPnl >= 0 ? '+' : '' }}{{ formatNum(data.item.realizedPnl, 4) }}
                </strong>
              </template>
              <template #cell(winRate)="data">
                <span :class="data.item.winRate >= 50 ? 'text-success' : 'text-warning'">
                  {{ formatNum(data.item.winRate, 1) }}%
                </span>
                <div class="font-small-2 text-muted">
                  {{ data.item.wins }}W / {{ data.item.losses }}L
                </div>
              </template>
              <template #cell(avgPnlPerTrade)="data">
                <span :class="data.item.avgPnlPerTrade >= 0 ? 'text-success' : 'text-danger'">
                  {{ formatNum(data.item.avgPnlPerTrade, 4) }}
                </span>
              </template>
              <template #cell(bestWorst)="data">
                <div class="font-small-3">
                  <span class="text-success">+{{ formatNum(data.item.bestPnl, 2) }}</span>
                  /
                  <span class="text-danger">{{ formatNum(data.item.worstPnl, 2) }}</span>
                </div>
              </template>
            </b-table>
          </b-card>
        </b-tab>

        <!-- ───────── TAB 2: Operasyon Defteri ───────── -->
        <b-tab
          title="Operasyon"
          @click="onTabOperations"
        >
          <b-card
            v-if="operationsLog"
            no-body
          >
            <b-card-body>
              <div class="d-flex align-items-center justify-content-between mb-1">
                <div>
                  <h5 class="mb-0">
                    Current Focus
                  </h5>
                  <small class="text-muted">Günlük snapshot ve sonraki çalışma noktası</small>
                </div>
                <b-badge variant="light-primary">
                  {{ operationsLog.currentFocus.activePhase || '-' }}
                </b-badge>
              </div>
              <b-row class="mb-1">
                <b-col
                  cols="6"
                  md="3"
                  class="mb-1"
                >
                  <div class="text-muted font-small-3">
                    Aktif alt iş
                  </div>
                  <strong>{{ operationsLog.currentFocus.activeSubtask || '-' }}</strong>
                </b-col>
                <b-col
                  cols="6"
                  md="3"
                  class="mb-1"
                >
                  <div class="text-muted font-small-3">
                    Son checkpoint
                  </div>
                  <strong>{{ operationsLog.currentFocus.lastCompletedCheckpoint || '-' }}</strong>
                </b-col>
                <b-col
                  cols="6"
                  md="3"
                  class="mb-1"
                >
                  <div class="text-muted font-small-3">
                    Sıradaki checkpoint
                  </div>
                  <strong>{{ operationsLog.currentFocus.nextCheckpoint || '-' }}</strong>
                </b-col>
                <b-col
                  cols="6"
                  md="3"
                  class="mb-1"
                >
                  <div class="text-muted font-small-3">
                    Faz sayısı
                  </div>
                  <strong>{{ (operationsLog.phaseStates && operationsLog.phaseStates.length) || 0 }}</strong>
                </b-col>
              </b-row>
              <b-row class="mb-1">
                <b-col
                  v-for="item in operationsLogLast7Summary"
                  :key="item.label"
                  cols="6"
                  md="3"
                  class="mb-1"
                >
                  <b-card no-body>
                    <b-card-body class="py-1 text-center">
                      <small class="text-muted">{{ item.label }}</small>
                      <h5 :class="item.className">
                        {{ item.value }}
                      </h5>
                    </b-card-body>
                  </b-card>
                </b-col>
              </b-row>
              <b-table
                :items="operationsLog.dailySnapshot || []"
                :fields="operationSnapshotFields"
                small
                responsive
                show-empty
                empty-text="Snapshot yok."
              />
              <div class="mt-2">
                <div class="text-muted font-small-3 mb-50">
                  Notlar
                </div>
                <div
                  v-for="note in operationsLog.notes || []"
                  :key="note"
                  class="mb-25"
                >
                  {{ note }}
                </div>
              </div>
            </b-card-body>
          </b-card>
          <b-alert
            v-else
            show
            variant="light-info"
          >
            Operasyon defteri yüklenmedi. Yenile butonunu kullan.
          </b-alert>
        </b-tab>

        <!-- ───────── TAB 3: Trade History ───────── -->
        <b-tab
          title="Trade History"
          @click="onTabTrades"
        >
          <b-card
            no-body
            class="mb-2"
          >
            <b-card-body class="py-1">
              <b-row>
                <b-col
                  cols="6"
                  md="2"
                  class="mb-1"
                >
                  <label class="font-small-3 text-muted">Bot</label>
                  <b-form-select
                    v-model="tradeFilter.botId"
                    :options="botFilterOptions"
                    @change="reloadTrades"
                  />
                </b-col>
                <b-col
                  cols="6"
                  md="2"
                  class="mb-1"
                >
                  <label class="font-small-3 text-muted">Sembol</label>
                  <b-form-input
                    v-model="tradeFilter.symbol"
                    placeholder="BTCUSDT"
                    @input="onTradeFilterDebounced"
                  />
                </b-col>
                <b-col
                  cols="6"
                  md="2"
                  class="mb-1"
                >
                  <label class="font-small-3 text-muted">Yön</label>
                  <b-form-select
                    v-model="tradeFilter.side"
                    :options="sideOptions"
                    @change="reloadTrades"
                  />
                </b-col>
                <b-col
                  cols="6"
                  md="2"
                  class="mb-1"
                >
                  <label class="font-small-3 text-muted">Aksiyon</label>
                  <b-form-select
                    v-model="tradeFilter.action"
                    :options="actionOptions"
                    @change="reloadTrades"
                  />
                </b-col>
                <b-col
                  cols="6"
                  md="2"
                  class="mb-1"
                >
                  <label class="font-small-3 text-muted">Başlangıç</label>
                  <b-form-input
                    v-model="tradeFilter.fromDate"
                    type="date"
                    @change="reloadTrades"
                  />
                </b-col>
                <b-col
                  cols="6"
                  md="2"
                  class="mb-1"
                >
                  <label class="font-small-3 text-muted">Bitiş</label>
                  <b-form-input
                    v-model="tradeFilter.toDate"
                    type="date"
                    @change="reloadTrades"
                  />
                </b-col>
              </b-row>
              <div class="d-flex align-items-center mt-1">
                <small class="text-muted">{{ tradesMeta.total }} trade</small>
                <b-button
                  variant="outline-secondary"
                  size="sm"
                  class="ml-auto mr-1"
                  @click="resetTradeFilter"
                >
                  <feather-icon icon="XIcon" /> Filtre Sıfırla
                </b-button>
                <b-button
                  variant="outline-success"
                  size="sm"
                  :disabled="!trades.length"
                  class="mr-1"
                  @click="exportTradesCsv"
                >
                  <feather-icon icon="DownloadIcon" /> Sayfa CSV
                </b-button>
                <b-button
                  variant="success"
                  size="sm"
                  :disabled="!tradesMeta.total || bulkExporting"
                  @click="exportAllTradesCsv"
                >
                  <b-spinner
                    v-if="bulkExporting"
                    small
                  />
                  <feather-icon
                    v-else
                    icon="DownloadCloudIcon"
                  />
                  Tümü CSV ({{ tradesMeta.total }})
                </b-button>
              </div>
            </b-card-body>
          </b-card>

          <b-card no-body>
            <b-table
              :items="trades"
              :fields="tradeListFields"
              small
              striped
              responsive
              show-empty
              empty-text="Bu filtreyle eşleşen trade yok."
            >
              <template #cell(occurredAt)="data">
                <span class="font-small-3">{{ formatDateTime(data.item.occurredAt) }}</span>
              </template>
              <template #cell(bot)="data">
                <span class="font-small-3">{{ data.item.botName }}</span>
                <div class="font-small-2 text-muted">
                  #{{ data.item.botId }}
                </div>
              </template>
              <template #cell(side)="data">
                <b-badge :variant="data.item.side === 'LONG' ? 'success' : 'danger'">
                  {{ data.item.side }}
                </b-badge>
              </template>
              <template #cell(action)="data">
                <b-badge :variant="data.item.action === 'ENTRY' ? 'info' : 'warning'">
                  {{ data.item.action }}
                </b-badge>
              </template>
              <template #cell(price)="data">
                {{ formatPrice(data.item.price) }}
              </template>
              <template #cell(qty)="data">
                {{ formatNum(data.item.qty, 6) }}
              </template>
              <template #cell(pnl)="data">
                <span
                  v-if="data.item.pnl !== null"
                  :class="data.item.pnl >= 0 ? 'text-success' : 'text-danger'"
                >
                  {{ data.item.pnl >= 0 ? '+' : '' }}{{ formatNum(data.item.pnl, 4) }}
                  <small v-if="data.item.pnlPercent !== null">({{ formatNum(data.item.pnlPercent, 2) }}%)</small>
                </span>
                <span
                  v-else
                  class="text-muted"
                >—</span>
              </template>
              <template #cell(reason)="data">
                <small class="text-muted">{{ truncate(data.item.reason, 40) }}</small>
              </template>
            </b-table>
            <div
              v-if="tradesMeta.totalPages > 1"
              class="d-flex justify-content-center py-1"
            >
              <b-pagination
                v-model="tradeFilter.page"
                :total-rows="tradesMeta.total"
                :per-page="tradeFilter.limit"
                @change="onTradePageChange"
              />
            </div>
          </b-card>
        </b-tab>

        <!-- ───────── TAB 4: Pozisyon Döngüsü ───────── -->
        <b-tab
          title="Pozisyon Döngüsü"
          @click="onTabPositionCycles"
        >
          <b-card
            no-body
            class="mb-2"
          >
            <b-card-body class="py-1">
              <b-row>
                <b-col
                  cols="6"
                  md="2"
                  class="mb-1"
                >
                  <label class="font-small-3 text-muted">Bot</label>
                  <b-form-select
                    v-model="cycleFilter.botId"
                    :options="botFilterOptions"
                    @change="reloadPositionCycles"
                  />
                </b-col>
                <b-col
                  cols="6"
                  md="2"
                  class="mb-1"
                >
                  <label class="font-small-3 text-muted">Sembol</label>
                  <b-form-input
                    v-model="cycleFilter.symbol"
                    placeholder="BTCUSDT"
                    @input="onCycleFilterDebounced"
                  />
                </b-col>
                <b-col
                  cols="6"
                  md="2"
                  class="mb-1"
                >
                  <label class="font-small-3 text-muted">Yön</label>
                  <b-form-select
                    v-model="cycleFilter.side"
                    :options="sideOptions"
                    @change="reloadPositionCycles"
                  />
                </b-col>
                <b-col
                  cols="6"
                  md="2"
                  class="mb-1"
                >
                  <label class="font-small-3 text-muted">Başlangıç</label>
                  <b-form-input
                    v-model="cycleFilter.fromDate"
                    type="date"
                    @change="reloadPositionCycles"
                  />
                </b-col>
                <b-col
                  cols="6"
                  md="2"
                  class="mb-1"
                >
                  <label class="font-small-3 text-muted">Bitiş</label>
                  <b-form-input
                    v-model="cycleFilter.toDate"
                    type="date"
                    @change="reloadPositionCycles"
                  />
                </b-col>
                <b-col
                  cols="6"
                  md="2"
                  class="mb-1 d-flex align-items-end"
                >
                  <b-button
                    variant="outline-secondary"
                    size="sm"
                    block
                    @click="resetCycleFilter"
                  >
                    <feather-icon icon="XIcon" /> Sıfırla
                  </b-button>
                </b-col>
              </b-row>
            </b-card-body>
          </b-card>

          <b-row
            v-if="positionCyclesSummary"
            class="mb-2"
          >
            <b-col
              cols="6"
              md="3"
              class="mb-1"
            >
              <b-card no-body>
                <b-card-body class="py-1 text-center">
                  <small class="text-muted">Net Döngü PnL</small>
                  <h4 :class="positionCyclesSummary.netPnl >= 0 ? 'text-success' : 'text-danger'">
                    {{ positionCyclesSummary.netPnl >= 0 ? '+' : '' }}{{ formatNum(positionCyclesSummary.netPnl, 4) }}
                  </h4>
                </b-card-body>
              </b-card>
            </b-col>
            <b-col
              cols="6"
              md="3"
              class="mb-1"
            >
              <b-card no-body>
                <b-card-body class="py-1 text-center">
                  <small class="text-muted">WR / Döngü</small>
                  <h4>{{ formatNum(positionCyclesSummary.winRate, 1) }}%</h4>
                  <small class="text-muted">{{ positionCyclesSummary.wins }}W / {{ positionCyclesSummary.losses }}L</small>
                </b-card-body>
              </b-card>
            </b-col>
            <b-col
              cols="6"
              md="3"
              class="mb-1"
            >
              <b-card no-body>
                <b-card-body class="py-1 text-center">
                  <small class="text-muted">Avg Win / Avg Loss</small>
                  <h5>
                    <span class="text-success">+{{ formatNum(positionCyclesSummary.avgWin, 3) }}</span>
                    /
                    <span class="text-danger">{{ formatNum(positionCyclesSummary.avgLoss, 3) }}</span>
                  </h5>
                </b-card-body>
              </b-card>
            </b-col>
            <b-col
              cols="6"
              md="3"
              class="mb-1"
            >
              <b-card no-body>
                <b-card-body class="py-1 text-center">
                  <small class="text-muted">PF / Expectancy</small>
                  <h5>{{ formatNum(positionCyclesSummary.profitFactor, 2) }} · {{ formatNum(positionCyclesSummary.expectancy, 3) }}</h5>
                  <small class="text-muted">Komisyon {{ formatNum(positionCyclesSummary.commission, 3) }}</small>
                </b-card-body>
              </b-card>
            </b-col>
          </b-row>

          <b-card no-body>
            <b-card-header class="d-flex align-items-center py-1">
              <h5 class="mb-0">
                <feather-icon icon="ActivityIcon" /> Pozisyon Döngüleri
              </h5>
              <small class="ml-auto text-muted">{{ positionCyclesMeta.total }} döngü</small>
            </b-card-header>
            <b-table
              :items="positionCycles"
              :fields="cycleFields"
              small
              striped
              responsive
              show-empty
              empty-text="Bu filtreyle eşleşen pozisyon döngüsü yok."
            >
              <template #cell(closedAt)="data">
                <span class="font-small-3">{{ formatDateTime(data.item.closedAt) }}</span>
                <div class="font-small-2 text-muted">
                  {{ formatDuration(data.item.durationMs) }}
                </div>
              </template>
              <template #cell(bot)="data">
                <span class="font-small-3">{{ data.item.botName }}</span>
                <div class="font-small-2 text-muted">
                  #{{ data.item.botId }} · Pos #{{ data.item.positionId || '—' }}
                </div>
              </template>
              <template #cell(symbol)="data">
                <strong>{{ data.item.symbol }}</strong>
                <div class="font-small-2 text-muted">
                  {{ data.item.side }} · {{ formatNum(data.item.leverage, 0) }}x
                </div>
              </template>
              <template #cell(netPnl)="data">
                <strong :class="data.item.netPnl >= 0 ? 'text-success' : 'text-danger'">
                  {{ data.item.netPnl >= 0 ? '+' : '' }}{{ formatNum(data.item.netPnl, 4) }}
                </strong>
                <div class="font-small-2 text-muted">
                  Brüt {{ formatNum(data.item.grossPnl, 3) }} · Kom {{ formatNum(data.item.commission, 3) }}
                </div>
              </template>
              <template #cell(exits)="data">
                <b-badge variant="light-primary">
                  {{ data.item.exitCount }} exit
                </b-badge>
                <b-badge
                  v-if="data.item.partialCount"
                  variant="light-success"
                  class="ml-50"
                >
                  {{ data.item.partialCount }} partial
                </b-badge>
              </template>
              <template #cell(reason)="data">
                <small class="text-muted">{{ truncate((data.item.reasons || []).join(' + '), 70) }}</small>
              </template>
            </b-table>
            <div
              v-if="positionCyclesMeta.totalPages > 1"
              class="d-flex justify-content-center py-1"
            >
              <b-pagination
                v-model="cycleFilter.page"
                :total-rows="positionCyclesMeta.total"
                :per-page="cycleFilter.limit"
                @change="onCyclePageChange"
              />
            </div>
          </b-card>
        </b-tab>

        <!-- ───────── TAB 5: Sembol Performansı ───────── -->
        <b-tab
          title="Sembol Performansı"
          @click="onTabSymbol"
        >
          <b-card
            v-if="symbolPerformance.length"
            no-body
            class="mb-2"
          >
            <b-card-body class="py-1">
              <h6 class="mb-1">
                Sembol PnL Karşılaştırma
              </h6>
              <vue-apex-charts
                type="bar"
                height="280"
                :options="symbolChartOptions"
                :series="symbolChartSeries"
              />
            </b-card-body>
          </b-card>
          <b-card no-body>
            <b-table
              :items="symbolPerformance"
              :fields="symbolFields"
              small
              striped
              responsive
              show-empty
              empty-text="Henüz trade verisi yok."
            >
              <template #cell(totalPnl)="data">
                <strong :class="data.item.totalPnl >= 0 ? 'text-success' : 'text-danger'">
                  {{ data.item.totalPnl >= 0 ? '+' : '' }}{{ formatNum(data.item.totalPnl, 4) }}
                </strong>
              </template>
              <template #cell(winRate)="data">
                <span :class="data.item.winRate >= 50 ? 'text-success' : 'text-warning'">
                  {{ formatNum(data.item.winRate, 1) }}%
                </span>
                <div class="font-small-2 text-muted">
                  {{ data.item.wins }}W / {{ data.item.losses }}L
                </div>
              </template>
              <template #cell(avgPnl)="data">
                <span :class="data.item.avgPnl >= 0 ? 'text-success' : 'text-danger'">
                  {{ formatNum(data.item.avgPnl, 4) }}
                </span>
              </template>
              <template #cell(bestWorst)="data">
                <div class="font-small-3">
                  <span class="text-success">+{{ formatNum(data.item.bestPnl, 2) }}</span>
                  /
                  <span class="text-danger">{{ formatNum(data.item.worstPnl, 2) }}</span>
                </div>
              </template>
            </b-table>
          </b-card>
        </b-tab>

        <!-- ───────── TAB 6: Hedef Tamamlama ───────── -->
        <b-tab
          title="Hedef Raporu"
          @click="onTabTargets"
        >
          <b-card no-body>
            <b-table
              :items="targetCompletion"
              :fields="targetFields"
              small
              striped
              responsive
              show-empty
              empty-text="Henüz hedef yok."
            >
              <template #cell(name)="data">
                <strong>{{ data.item.name }}</strong>
                <div class="font-small-2 text-muted">
                  #{{ data.item.targetId }} · {{ data.item.attachedBots }} bot
                </div>
              </template>
              <template #cell(status)="data">
                <b-badge :variant="targetStatusVariant(data.item.status)">
                  {{ data.item.status }}
                </b-badge>
                <div
                  v-if="data.item.cycleCount > 0"
                  class="font-small-2 text-muted mt-1"
                >
                  Cycle #{{ data.item.cycleCount }}
                </div>
              </template>
              <template #cell(progress)="data">
                <strong :class="data.item.realizedPnl >= 0 ? 'text-success' : 'text-danger'">
                  {{ data.item.realizedPnl >= 0 ? '+' : '' }}{{ formatNum(data.item.realizedPnl, 4) }}
                </strong>
                <div class="font-small-2 text-muted">
                  / {{ formatNum(data.item.profitTarget, 2) }}{{ data.item.profitTargetIsPct ? '%' : '$' }}
                </div>
              </template>
              <template #cell(initialCapital)="data">
                <span class="font-small-3">{{ formatNum(data.item.initialCapital, 2) }}</span>
              </template>
              <template #cell(durationMs)="data">
                <span
                  v-if="data.item.durationMs"
                  class="font-small-3"
                >
                  {{ formatDuration(data.item.durationMs) }}
                </span>
                <span
                  v-else
                  class="text-muted"
                >—</span>
              </template>
              <template #cell(timeline)="data">
                <div
                  v-if="data.item.startedAt"
                  class="font-small-3"
                >
                  <feather-icon icon="PlayIcon" /> {{ formatDateTime(data.item.startedAt) }}
                </div>
                <div
                  v-if="data.item.completedAt"
                  class="font-small-3 text-muted"
                >
                  <feather-icon icon="CheckIcon" /> {{ formatDateTime(data.item.completedAt) }}
                </div>
              </template>
            </b-table>
          </b-card>
        </b-tab>

      </b-tabs>
    </b-card>
  </div>
</template>

<script>
import {
  BCard, BCardBody, BCardHeader, BRow, BCol, BTable, BBadge, BButton, BFormSelect,
  BTabs, BTab, BFormInput, BPagination, BSpinner,
} from 'bootstrap-vue'
import VueApexCharts from 'vue-apexcharts'

export default {
  name: 'ReportsIndex',
  components: {
    BCard,
    BCardBody,
    BCardHeader,
    BRow,
    BCol,
    BTable,
    BBadge,
    BButton,
    BFormSelect,
    BTabs,
    BTab,
    BFormInput,
    BPagination,
    BSpinner,
    VueApexCharts,
  },
  data() {
    return {
      loading: false,
      activeTab: 0,
      timelineDays: 30,
      dayOptions: [
        { value: 7, text: '7 gün' },
        { value: 14, text: '14 gün' },
        { value: 30, text: '30 gün' },
        { value: 60, text: '60 gün' },
        { value: 90, text: '90 gün' },
      ],
      botFields: [
        { key: 'name', label: 'Bot' },
        { key: 'realizedPnl', label: 'Realize PnL', thClass: 'width-130' },
        { key: 'winRate', label: 'Win Rate', thClass: 'width-110' },
        { key: 'exits', label: 'Kapanan', thClass: 'width-90' },
        { key: 'avgPnlPerTrade', label: 'Avg/Trade', thClass: 'width-110' },
        { key: 'bestWorst', label: 'Best / Worst', thClass: 'width-160' },
      ],
      tradeListFields: [
        { key: 'occurredAt', label: 'Zaman', thClass: 'width-150' },
        { key: 'bot', label: 'Bot' },
        { key: 'symbol', label: 'Sembol' },
        { key: 'side', label: 'Yön' },
        { key: 'action', label: 'Aksiyon' },
        { key: 'price', label: 'Fiyat' },
        { key: 'qty', label: 'Miktar' },
        { key: 'pnl', label: 'PnL' },
        { key: 'reason', label: 'Sebep' },
      ],
      cycleFields: [
        { key: 'closedAt', label: 'Kapanış', thClass: 'width-150' },
        { key: 'bot', label: 'Bot' },
        { key: 'symbol', label: 'Sembol' },
        { key: 'netPnl', label: 'Net PnL', thClass: 'width-150' },
        { key: 'exits', label: 'Çıkışlar', thClass: 'width-130' },
        { key: 'reason', label: 'Sebep' },
      ],
      symbolFields: [
        { key: 'symbol', label: 'Sembol' },
        { key: 'totalPnl', label: 'Toplam PnL', thClass: 'width-130' },
        { key: 'trades', label: 'Toplam Trade', thClass: 'width-110' },
        { key: 'exits', label: 'Kapanan', thClass: 'width-100' },
        { key: 'winRate', label: 'Win Rate', thClass: 'width-110' },
        { key: 'avgPnl', label: 'Avg/Trade', thClass: 'width-110' },
        { key: 'bestWorst', label: 'Best / Worst', thClass: 'width-160' },
      ],
      targetFields: [
        { key: 'name', label: 'Hedef' },
        { key: 'status', label: 'Durum', thClass: 'width-120' },
        { key: 'progress', label: 'PnL / Hedef', thClass: 'width-160' },
        { key: 'initialCapital', label: 'Sermaye', thClass: 'width-110' },
        { key: 'durationMs', label: 'Süre', thClass: 'width-130' },
        { key: 'timeline', label: 'Tarih', thClass: 'width-180' },
      ],
      tradeFilter: {
        botId: null,
        symbol: '',
        side: null,
        action: null,
        fromDate: '',
        toDate: '',
        page: 1,
        limit: 50,
      },
      cycleFilter: {
        botId: null,
        symbol: '',
        side: null,
        fromDate: '',
        toDate: '',
        page: 1,
        limit: 50,
      },
      sideOptions: [
        { value: null, text: 'Hepsi' },
        { value: 'LONG', text: 'LONG' },
        { value: 'SHORT', text: 'SHORT' },
      ],
      actionOptions: [
        { value: null, text: 'Hepsi' },
        { value: 'ENTRY', text: 'ENTRY' },
        { value: 'EXIT', text: 'EXIT' },
      ],
      tradeFilterDebounceTimer: null,
      cycleFilterDebounceTimer: null,
      bulkExporting: false,
      operationSnapshotFields: [
        { key: 'Date', label: 'Date' },
        { key: 'Phase', label: 'Phase' },
        { key: 'Subtask', label: 'Subtask' },
        { key: 'Summary', label: 'Summary' },
        { key: 'Net PnL', label: 'Net PnL' },
        { key: 'WR', label: 'WR' },
        { key: 'PF', label: 'PF' },
        { key: 'Expectancy', label: 'Expectancy' },
        { key: 'Avg Win/Loss', label: 'Avg Win/Loss' },
        { key: 'Commission', label: 'Commission' },
        { key: 'Guardian Actions', label: 'Guardian Actions' },
        { key: 'Next Step', label: 'Next Step' },
      ],
    }
  },
  computed: {
    summary() { return this.$store.getters['reports/summary'] },
    pnlTimeline() { return this.$store.getters['reports/pnlTimeline'] },
    botPerformance() { return this.$store.getters['reports/botPerformance'] },
    operationsLog() { return this.$store.getters['reports/operationsLog'] },
    operationsLogLast7Summary() {
      const rows = (this.operationsLog?.dailySnapshot || []).slice(-7)
      const parse = key => rows.map(row => Number(String(row[key] ?? '').replace('%', '').replace(',', '.'))).filter(Number.isFinite)
      const net = parse('Net PnL')
      const wr = parse('WR')
      const pf = parse('PF')
      const comm = parse('Commission')
      const actions = parse('Guardian Actions')
      const sum = arr => arr.reduce((a, b) => a + b, 0)
      const avg = arr => (arr.length ? sum(arr) / arr.length : 0)
      return [
        { label: 'Son 7 gün satır', value: `${rows.length}`, className: 'text-primary' },
        { label: 'Net PnL', value: rows.length ? `${sum(net) >= 0 ? '+' : ''}${this.formatNum(sum(net), 2)}` : '-', className: sum(net) >= 0 ? 'text-success' : 'text-danger' },
        { label: 'Ort. WR', value: wr.length ? `${this.formatNum(avg(wr), 1)}%` : '-', className: avg(wr) >= 50 ? 'text-success' : 'text-warning' },
        { label: 'Ort. PF', value: pf.length ? this.formatNum(avg(pf), 2) : '-', className: avg(pf) >= 1 ? 'text-success' : 'text-warning' },
        { label: 'Komisyon', value: comm.length ? this.formatNum(sum(comm), 2) : '-', className: 'text-muted' },
        { label: 'Guardian', value: actions.length ? this.formatNum(sum(actions), 0) : '0', className: 'text-info' },
      ]
    },
    pnlChartOptions() {
      return {
        chart: { toolbar: { show: false }, sparkline: { enabled: false } },
        plotOptions: {
          bar: {
            columnWidth: '70%',
            distributed: false,
            colors: {
              ranges: [
                { from: -1e12, to: 0, color: '#ea5455' },
                { from: 0, to: 1e12, color: '#28c76f' },
              ],
            },
          },
        },
        dataLabels: { enabled: false },
        xaxis: {
          categories: this.pnlTimeline.map(p => p.date.slice(5)), // MM-DD
          labels: { rotate: -45, style: { fontSize: '10px' } },
        },
        yaxis: { labels: { formatter: v => Number(v).toFixed(2) } },
        tooltip: {
          custom: ({ dataPointIndex }) => {
            const p = this.pnlTimeline[dataPointIndex]
            if (!p) return ''
            return `<div class="px-1 py-1">
              <div><strong>${p.date}</strong></div>
              <div>PnL: <strong>${p.pnl.toFixed(4)}</strong></div>
              <div>Trade: ${p.trades} (${p.wins}W / ${p.losses}L)</div>
            </div>`
          },
        },
        grid: { borderColor: '#e0e0e0' },
      }
    },
    pnlChartSeries() {
      return [{ name: 'PnL', data: this.pnlTimeline.map(p => Number(p.pnl.toFixed(4))) }]
    },
    equityCurve() {
      // Kümülatif PnL — pnlTimeline'dan client-side hesaplanır
      let cum = 0
      return this.pnlTimeline.map(p => {
        cum += Number(p.pnl)
        return { date: p.date, cum: Number(cum.toFixed(4)) }
      })
    },
    equityChartOptions() {
      const series = this.equityCurve
      const lastVal = series.length ? series[series.length - 1].cum : 0
      const positive = lastVal >= 0
      return {
        chart: { toolbar: { show: false }, sparkline: { enabled: false }, zoom: { enabled: false } },
        stroke: { curve: 'smooth', width: 2 },
        colors: [positive ? '#28c76f' : '#ea5455'],
        fill: {
          type: 'gradient',
          gradient: {
            shadeIntensity: 1, opacityFrom: 0.4, opacityTo: 0.05, stops: [0, 100],
          },
        },
        dataLabels: { enabled: false },
        xaxis: {
          categories: series.map(p => p.date.slice(5)),
          labels: { rotate: -45, style: { fontSize: '10px' } },
        },
        yaxis: { labels: { formatter: v => Number(v).toFixed(2) } },
        grid: { borderColor: '#e0e0e0' },
        markers: { size: 0 },
        tooltip: {
          custom: ({ dataPointIndex }) => {
            const p = series[dataPointIndex]
            if (!p) return ''
            return `<div class="px-1 py-1">
              <div><strong>${p.date}</strong></div>
              <div>Kümülatif: <strong>${p.cum.toFixed(4)}</strong></div>
            </div>`
          },
        },
      }
    },
    equityChartSeries() {
      return [{ name: 'Kümülatif PnL', data: this.equityCurve.map(p => p.cum) }]
    },
    trades() { return this.$store.getters['reports/trades'] },
    tradesMeta() { return this.$store.getters['reports/tradesMeta'] },
    positionCycles() { return this.$store.getters['reports/positionCycles'] },
    positionCyclesSummary() { return this.$store.getters['reports/positionCyclesSummary'] },
    positionCyclesMeta() { return this.$store.getters['reports/positionCyclesMeta'] },
    symbolPerformance() { return this.$store.getters['reports/symbolPerformance'] },
    targetCompletion() { return this.$store.getters['reports/targetCompletion'] },
    performanceSummary() { return this.$store.getters['reports/performanceSummary'] },
    performanceSummaryCards() {
      const s = this.performanceSummary
      if (!s) return []
      return [
        { label: 'Net PnL', value: `${s.netPnl >= 0 ? '+' : ''}${this.formatNum(s.netPnl, 4)}`, className: s.netPnl >= 0 ? 'text-success' : 'text-danger' },
        { label: 'Win Rate', value: `${this.formatNum(s.winRate, 1)}%`, className: s.winRate >= 50 ? 'text-success' : 'text-warning' },
        { label: 'Profit Factor', value: this.formatNum(s.profitFactor, 2), className: s.profitFactor >= 1 ? 'text-success' : 'text-warning' },
        { label: 'Expectancy', value: this.formatNum(s.expectancy, 4), className: s.expectancy >= 0 ? 'text-success' : 'text-danger' },
        { label: 'Ort. Win / Loss', value: `${this.formatNum(s.avgWin, 4)} / ${this.formatNum(s.avgLoss, 4)}`, className: 'text-info' },
        { label: 'Komisyon', value: this.formatNum(s.commission, 4), className: 'text-muted' },
        { label: 'Maks. Drawdown', value: `${this.formatNum(s.drawdownPct, 2)}%`, className: s.drawdownPct <= 10 ? 'text-success' : 'text-warning' },
        { label: 'Guardian', value: `${s.guardianActions}`, className: 'text-primary' },
      ]
    },
    botFilterOptions() {
      // Bot performance verilerinden bot listesi çek (zaten yüklü)
      const opts = [{ value: null, text: 'Tüm botlar' }]
      for (const b of this.botPerformance) {
        opts.push({ value: b.botId, text: b.name })
      }
      return opts
    },
    symbolChartOptions() {
      return {
        chart: { toolbar: { show: false } },
        plotOptions: { bar: { distributed: true, horizontal: true, barHeight: '70%' } },
        dataLabels: { enabled: false },
        colors: this.symbolPerformance.map(s => (s.totalPnl >= 0 ? '#28c76f' : '#ea5455')),
        xaxis: { categories: this.symbolPerformance.map(s => s.symbol) },
        legend: { show: false },
        grid: { borderColor: '#e0e0e0' },
        tooltip: {
          custom: ({ dataPointIndex }) => {
            const s = this.symbolPerformance[dataPointIndex]
            if (!s) return ''
            return `<div class="px-1 py-1">
              <div><strong>${s.symbol}</strong></div>
              <div>PnL: <strong>${s.totalPnl.toFixed(4)}</strong></div>
              <div>Win Rate: ${s.winRate.toFixed(1)}%</div>
              <div>Trade: ${s.exits} kapanan / ${s.trades} toplam</div>
            </div>`
          },
        },
      }
    },
    symbolChartSeries() {
      return [{
        name: 'PnL',
        data: this.symbolPerformance.map(s => Number(s.totalPnl.toFixed(4))),
      }]
    },
  },
  created() {
    this.fetchAll()
  },
  methods: {
    async fetchAll() {
      this.loading = true
      await Promise.all([
        this.$store.dispatch('reports/getSummary'),
        this.$store.dispatch('reports/getPerformanceSummary', { days: 7 }),
        this.fetchTimeline(),
        this.$store.dispatch('reports/getBotPerformance', { limit: 50 }),
        this.$store.dispatch('reports/getOperationsLog'),
      ])
      this.loading = false
    },
    fetchTimeline() {
      return this.$store.dispatch('reports/getPnlTimeline', { days: this.timelineDays })
    },

    // ─── Tab triggers (lazy load) ────────────────────────────────────────
    onTabSummary() { /* zaten loaded */ },
    onTabOperations() {
      this.$store.dispatch('reports/getOperationsLog')
    },
    onTabTrades() {
      this.reloadTrades()
    },
    onTabPositionCycles() {
      this.reloadPositionCycles()
    },
    onTabSymbol() {
      this.$store.dispatch('reports/getSymbolPerformance')
    },
    onTabTargets() {
      this.$store.dispatch('reports/getTargetCompletion')
    },
    onRefresh() {
      this.fetchAll()
      this.$store.dispatch('reports/getPerformanceSummary', { days: 7 })
      this.$store.dispatch('reports/getSymbolPerformance')
      this.$store.dispatch('reports/getTargetCompletion')
      this.$store.dispatch('reports/getOperationsLog')
      this.reloadTrades()
      this.reloadPositionCycles()
    },

    // ─── Trade filter actions ────────────────────────────────────────────
    reloadTrades() {
      const params = {
        botId: this.tradeFilter.botId || undefined,
        symbol: this.tradeFilter.symbol || undefined,
        side: this.tradeFilter.side || undefined,
        action: this.tradeFilter.action || undefined,
        fromDate: this.tradeFilter.fromDate || undefined,
        toDate: this.tradeFilter.toDate || undefined,
        page: this.tradeFilter.page,
        limit: this.tradeFilter.limit,
      }
      return this.$store.dispatch('reports/getTrades', params)
    },
    onTradeFilterDebounced() {
      clearTimeout(this.tradeFilterDebounceTimer)
      this.tradeFilterDebounceTimer = setTimeout(() => {
        this.tradeFilter.page = 1
        this.reloadTrades()
      }, 350)
    },
    onTradePageChange(page) {
      this.tradeFilter.page = page
      this.reloadTrades()
    },
    resetTradeFilter() {
      this.tradeFilter = {
        botId: null,
        symbol: '',
        side: null,
        action: null,
        fromDate: '',
        toDate: '',
        page: 1,
        limit: 50,
      }
      this.reloadTrades()
    },

    // ─── Position-cycle filter actions ─────────────────────────────────
    reloadPositionCycles() {
      const params = {
        botId: this.cycleFilter.botId || undefined,
        symbol: this.cycleFilter.symbol || undefined,
        side: this.cycleFilter.side || undefined,
        fromDate: this.cycleFilter.fromDate || undefined,
        toDate: this.cycleFilter.toDate || undefined,
        page: this.cycleFilter.page,
        limit: this.cycleFilter.limit,
      }
      return this.$store.dispatch('reports/getPositionCycles', params)
    },
    onCycleFilterDebounced() {
      clearTimeout(this.cycleFilterDebounceTimer)
      this.cycleFilterDebounceTimer = setTimeout(() => {
        this.cycleFilter.page = 1
        this.reloadPositionCycles()
      }, 350)
    },
    onCyclePageChange(page) {
      this.cycleFilter.page = page
      this.reloadPositionCycles()
    },
    resetCycleFilter() {
      this.cycleFilter = {
        botId: null,
        symbol: '',
        side: null,
        fromDate: '',
        toDate: '',
        page: 1,
        limit: 50,
      }
      this.reloadPositionCycles()
    },

    // ─── Bulk CSV export (server-side, all filtered rows) ──────────────
    async exportAllTradesCsv() {
      this.bulkExporting = true
      const params = {
        botId: this.tradeFilter.botId || undefined,
        symbol: this.tradeFilter.symbol || undefined,
        side: this.tradeFilter.side || undefined,
        action: this.tradeFilter.action || undefined,
        fromDate: this.tradeFilter.fromDate || undefined,
        toDate: this.tradeFilter.toDate || undefined,
      }
      try {
        await this.$store.dispatch('reports/exportTradesCsv', params)
      } catch (e) {
        console.error('bulk export failed', e)
      } finally {
        this.bulkExporting = false
      }
    },

    // ─── Page CSV export (client-side, current paginated data) ──────────
    exportTradesCsv() {
      if (!this.trades.length) return
      const headers = ['id', 'occurredAt', 'botId', 'botName', 'symbol', 'side', 'action', 'price', 'qty', 'leverage', 'pnl', 'pnlPercent', 'commission', 'reason']
      const rows = this.trades.map(t => headers.map(h => {
        const v = t[h]
        if (v === null || v === undefined) return ''
        if (h === 'occurredAt') return new Date(v).toISOString()
        if (typeof v === 'string' && (v.includes(',') || v.includes('"') || v.includes('\n'))) {
          return `"${v.replace(/"/g, '""')}"`
        }
        return String(v)
      }).join(','))
      const csv = [headers.join(','), ...rows].join('\n')
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `trades-${new Date().toISOString().slice(0, 10)}.csv`
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      URL.revokeObjectURL(url)
    },

    // ─── Format helpers ──────────────────────────────────────────────────
    formatNum(v, d = 2) {
      if (v === null || v === undefined) return '—'
      const n = Number(v)
      return Number.isFinite(n) ? n.toFixed(d) : '—'
    },
    formatPrice(v) {
      if (v === null || v === undefined) return '—'
      const n = Number(v)
      if (!Number.isFinite(n)) return '—'
      let d = 4
      if (n >= 1000) d = 2; else if (n < 1) d = 6
      return n.toLocaleString('tr-TR', { minimumFractionDigits: d, maximumFractionDigits: d })
    },
    formatDateTime(v) {
      if (!v) return '—'
      try {
        const d = new Date(v)
        if (isNaN(d.getTime())) return String(v)
        return d.toLocaleString('tr-TR', {
          day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit',
        })
      } catch (e) { return String(v) }
    },
    formatDuration(ms) {
      if (!ms || ms < 0) return '—'
      const s = Math.floor(ms / 1000)
      const days = Math.floor(s / 86400)
      const hours = Math.floor((s % 86400) / 3600)
      const mins = Math.floor((s % 3600) / 60)
      if (days > 0) return `${days}g ${hours}s`
      if (hours > 0) return `${hours}s ${mins}d`
      return `${mins}d`
    },
    truncate(s, n) { return s && s.length > n ? `${s.slice(0, n)}...` : (s || '—') },
    performanceBandVariant(band) {
      if (band === 'HEALTHY') return 'success'
      if (band === 'WATCH') return 'warning'
      if (band === 'RISKY') return 'danger'
      return 'dark'
    },
    botStatusVariant(s) {
      const map = {
        IDLE: 'secondary', RUNNING: 'success', PAUSED: 'warning', STOPPED: 'dark', ERROR: 'danger',
      }
      return map[s] || 'secondary'
    },
    targetStatusVariant(s) {
      const map = {
        DRAFT: 'secondary', ACTIVE: 'success', COMPLETED: 'info', STOPPED: 'dark', FAILED: 'danger',
      }
      return map[s] || 'secondary'
    },
  },
}
</script>
